/**
 * /destinations/$destinationId — the View page (plan §5.5, D15). Config
 * summary (never the URL), error banner, actions (test message, pause/resume,
 * disconnect), and run history. Disconnect lives HERE, per the sources
 * doctrine — the index is a pure index.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Pause, Play, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/patterns/empty-state';
import { PageHeader } from '@/components/patterns/page-header';
import { StatusPill } from '@/components/patterns/status-pill';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type DestinationRun, destinationsApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

function RunRow({ run, destinationId }: { run: DestinationRun; destinationId: string }) {
  const stats = run.stats as { delivered?: number; suppressed?: number; candidates?: number };
  return (
    <Link
      to="/destinations/$destinationId/runs/$runId"
      params={{ destinationId, runId: run.id }}
      className="flex items-center gap-4 rounded-xl border border-border px-4 py-3 transition-colors hover:border-primary"
    >
      <StatusPill
        tone={run.status === 'succeeded' ? 'active' : run.status === 'failed' ? 'danger' : 'paused'}
      >
        {run.status}
      </StatusPill>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {new Date(run.startedAt).toLocaleString()}
          {run.dryRun ? ' · dry run' : ''}
        </p>
        <p className="truncate text-[13px] text-faint">
          {stats.candidates ?? 0} candidates · {stats.suppressed ?? 0} suppressed
          {run.degradations.length > 0 ? ` · ⚠ ${run.degradations.join(', ')}` : ''}
        </p>
      </div>
    </Link>
  );
}

export function DestinationDetailPage() {
  const { destinationId = '' } = useParams({ strict: false }) as { destinationId?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [confirmingRun, setConfirmingRun] = useState(false);

  const { data: destination, isLoading } = useQuery({
    queryKey: qk.destination(destinationId),
    queryFn: () => destinationsApi.get(destinationId),
  });
  const { data: runs } = useQuery({
    queryKey: qk.destinationRuns(destinationId),
    queryFn: () => destinationsApi.listRuns(destinationId),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === 'running') ? 2_000 : false,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.destination(destinationId) });
    void qc.invalidateQueries({ queryKey: qk.destinations });
    void qc.invalidateQueries({ queryKey: qk.destinationRuns(destinationId) });
  };

  const dryRun = useMutation({
    mutationFn: () => destinationsApi.createRun(destinationId, { dryRun: true }),
    onSuccess: () => {
      toast.success('Dry run started — records appear below as the pipeline finishes.');
      invalidate();
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { reason?: string } | null) : null;
      toast.error(body?.reason ?? 'Could not start the dry run.');
    },
  });

  const realRun = useMutation({
    mutationFn: () => destinationsApi.createRun(destinationId, { dryRun: false }),
    onSuccess: () => {
      setConfirmingRun(false);
      toast.success('Run started — proposals are being posted to the channel.');
      invalidate();
    },
    onError: (err) => {
      setConfirmingRun(false);
      const body = err instanceof ApiError ? (err.body as { reason?: string } | null) : null;
      toast.error(body?.reason ?? 'Could not start the run.');
    },
  });

  const test = useMutation({
    mutationFn: () => destinationsApi.sendTest(destinationId),
    onSuccess: () => {
      toast.success('Test message sent — check the channel.');
      invalidate();
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { reason?: string } | null) : null;
      toast.error(body?.reason ?? 'Test message failed.');
      invalidate();
    },
  });

  const togglePause = useMutation({
    mutationFn: (status: 'active' | 'paused') => destinationsApi.patch(destinationId, { status }),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Could not update the destination.'),
  });

  const disconnect = useMutation({
    mutationFn: () => destinationsApi.disconnect(destinationId),
    onSuccess: () => {
      toast.success('Destination disconnected.');
      void qc.invalidateQueries({ queryKey: qk.destinations });
      void navigate({ to: '/destinations' });
    },
    onError: () => toast.error('Could not disconnect.'),
  });

  if (isLoading || !destination) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[160px] w-full rounded-xl" />
      </div>
    );
  }

  const scheduleText = destination.schedule
    ? `${destination.schedule.runs.map((r) => r.at).join(', ')} · ${destination.schedule.tz}`
    : 'No schedule';

  return (
    <div>
      <Link
        to="/destinations"
        className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'mb-4 -ml-2')}
      >
        <ArrowLeft className="h-4 w-4" />
        Destinations
      </Link>

      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={destination.label ?? 'Slack webhook'}
          subtitle={`${destination.host ?? '—'} · ${scheduleText}`}
        />
        <StatusPill
          tone={
            destination.status === 'active'
              ? 'active'
              : destination.status === 'error'
                ? 'danger'
                : 'paused'
          }
        >
          {destination.status}
        </StatusPill>
      </div>

      {destination.lastError ? (
        <div className="mt-6 rounded-xl border border-destructive-border bg-destructive-bg px-4 py-3">
          <p className="text-sm font-medium text-destructive">Something needs attention</p>
          <p className="mt-1 text-sm text-destructive">{destination.lastError}</p>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          disabled={dryRun.isPending || destination.status === 'disconnected'}
          onClick={() => dryRun.mutate()}
        >
          {dryRun.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          Dry run
        </Button>
        {/* A real run posts to the channel and cannot be undone (Slack
            incoming webhooks have no delete), so it asks first. */}
        {confirmingRun ? (
          <>
            <Button
              size="sm"
              variant="destructive"
              disabled={realRun.isPending}
              onClick={() => realRun.mutate()}
            >
              {realRun.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Post to the channel — this cannot be undone'
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingRun(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={destination.status === 'disconnected' || destination.status === 'paused'}
            onClick={() => setConfirmingRun(true)}
          >
            <Send className="h-4 w-4" />
            Run now
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={test.isPending || destination.status === 'disconnected'}
          onClick={() => test.mutate()}
        >
          {test.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Send test message
        </Button>
        {destination.status === 'paused' ? (
          <Button
            variant="outline"
            size="sm"
            disabled={togglePause.isPending}
            onClick={() => togglePause.mutate('active')}
          >
            <Play className="h-4 w-4" />
            Resume
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={togglePause.isPending || destination.status === 'disconnected'}
            onClick={() => togglePause.mutate('paused')}
          >
            <Pause className="h-4 w-4" />
            Pause
          </Button>
        )}
        {confirmingDisconnect ? (
          <>
            <Button
              variant="destructive"
              size="sm"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              {disconnect.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Confirm disconnect'
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingDisconnect(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={destination.status === 'disconnected'}
            onClick={() => setConfirmingDisconnect(true)}
          >
            <Trash2 className="h-4 w-4" />
            Disconnect
          </Button>
        )}
      </div>

      <h2 className="mt-10 text-[15px] font-semibold">Run history</h2>
      <p className="mt-1 text-[13px] text-faint">
        Every run — dry or real — with its counts, degradations, and every record&apos;s keep/drop
        reason. Dry runs render everything and post nothing; scheduled runs post for real.
      </p>
      {!runs || runs.length === 0 ? (
        <EmptyState
          className="mt-6"
          icon={Send}
          title="No runs yet"
          body="Hit Dry run to distill the recent window and review exactly what Brain would post — before anything reaches the channel."
        />
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {runs.map((run) => (
            <RunRow key={run.id} run={run} destinationId={destinationId} />
          ))}
        </div>
      )}
    </div>
  );
}
